import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { API } from "@/assets";
import { Mic2, MapPin, AudioWaveform, ArrowRight, BadgeCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);

const useTitle = (title) => {
  useEffect(() => {
    document.title = `${title} | DGE: Cold-Blooded Records LLC™`;
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, [title]);
};

export default function RosterPage() {
  useTitle("The Roster");
  const [artists, setArtists] = useState(null);

  useEffect(() => {
    fetch(`${API}/roster`)
      .then((r) => r.json())
      .then((d) => setArtists(d.artists || []))
      .catch(() => setArtists([]));
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="roster-page">
      <Navbar />
      <div className="pt-[70px]">
        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <Reveal className="text-center">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4 flex items-center justify-center gap-2">
              <BadgeCheck size={14} /> Signed &amp; Certified
            </p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08]">
              The Roster.
            </h1>
            <p className="mt-4 text-sm sm:text-base text-ice/60 leading-relaxed max-w-xl mx-auto">
              Artists officially A&amp;R-approved by DGE: Cold-Blooded Records LLC™ leadership.
              Every name here earned it — no pay-to-play, no shortcuts.
            </p>
          </Reveal>

          {artists === null ? (
            <p className="mt-16 text-center text-sm text-ice/40" data-testid="roster-loading">Loading the roster…</p>
          ) : artists.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="mt-14 max-w-xl mx-auto bg-charcoal border border-crystal/25 rounded-2xl p-10 text-center" data-testid="roster-empty">
                <Mic2 size={32} className="text-venom mx-auto" />
                <h2 className="mt-4 font-display text-xl font-bold text-white">The roster is forming.</h2>
                <p className="mt-3 text-sm text-ice/60 leading-relaxed">
                  Our A&amp;R team is reviewing submissions right now. The first signed artists
                  will be announced here — think you belong on this wall?
                </p>
                <Link
                  to="/talent"
                  className="mt-7 inline-flex items-center gap-2 bg-venom text-obsidian font-bold text-sm px-7 py-3.5 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 transition-all duration-300"
                  data-testid="roster-submit-cta"
                >
                  Submit Demo to A&amp;R <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="roster-grid">
              {artists.map((a, i) => (
                <Reveal key={a.id} delay={Math.min(i, 6) * 0.08} className="h-full">
                  <div
                    className="group h-full bg-charcoal border border-crystal/20 rounded-2xl overflow-hidden hover:border-venom/60 hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgba(57,255,20,0.14)] transition-all duration-500"
                    data-testid={`roster-card-${i}`}
                  >
                    <div className="relative aspect-square bg-obsidian/60 overflow-hidden">
                      {a.photo ? (
                        <img src={mediaUrl(a.photo)} alt={a.stage_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Mic2 size={44} className="text-crystal/40" />
                        </div>
                      )}
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] bg-venom text-obsidian font-bold rounded-full px-2.5 py-1">
                        <BadgeCheck size={11} /> Signed {a.since}
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-bold text-white">{a.stage_name}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ice/55">
                        {a.genre && <span className="inline-flex items-center gap-1.5"><AudioWaveform size={12} className="text-venom" />{a.genre}</span>}
                        {a.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-crystal" />{a.location}</span>}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal delay={0.15}>
            <p className="mt-12 text-center text-[11px] font-mono uppercase tracking-[0.2em] text-ice/35">
              Roster updates automatically the moment A&amp;R approves a submission
            </p>
          </Reveal>
        </section>
      </div>
      <Footer />
    </main>
  );
}
